import {
    CON_COMMAND,
    ConDialogBlock,
    ConEntry,
} from "../loaders/con";

export type ConConditionEvaluator = (name: string) => boolean | undefined;
export type ConActionRunner = (name: string) => any;

/**
 * Executes the editable block graph recovered from an original ROSE .CON file.
 *
 * A NPCSAY entry selects the current NPC message and points at the next
 * block. CLOSE, PLAYERSELECT, and JUMPSELECT entries in that next block are
 * the player's visible choices. The selected choice either closes the
 * conversation or points at another NPC message block.
 */
export class ConConversationState {
    private readonly blocks: ConDialogBlock[];
    private readonly initialEntry?: ConEntry;
    private readonly strings: Record<number, string>;
    private readonly evaluateCondition: ConConditionEvaluator;
    private readonly runAction: ConActionRunner;
    private optionEntries = new Map<number, ConEntry>();

    message = "";
    options: Record<number, string> = {};
    closed = false;

    constructor(
        spec: any,
        langId: string | number,
        evaluateCondition: ConConditionEvaluator = () => undefined,
        runAction: ConActionRunner = () => {},
    ) {
        this.blocks = spec.dialogBlocks || [];
        this.initialEntry = spec.initEntries?.[0];
        this.strings = spec.strings?.[langId] || {};
        this.evaluateCondition = evaluateCondition;
        this.runAction = runAction;
    }

    start(): void {
        if (this.initialEntry?.conditionFunction) {
            const available = this.isAvailable(this.initialEntry);
            if (available === false) {
                this.closed = true;
                return;
            }
        }
        if (!this.openBlock(0)) {
            this.close();
        }
    }

    pickOption(optionId: number): void {
        if (this.closed) return;

        const entry = this.optionEntries.get(optionId);
        if (!entry) {
            throw new Error(`Unknown CON dialog option ${optionId}`);
        }

        this.runReward(entry);
        if (entry.command === CON_COMMAND.CLOSE || entry.data === 0xffffffff) {
            this.finish();
            return;
        }

        if (!this.openBlock(entry.data)) {
            this.finish();
        }
    }

    private openBlock(initialBlockIndex: number): boolean {
        let blockIndex = initialBlockIndex;

        // NPC message blocks may chain directly to another message block.
        // Keep a hard guard because corrupt files must not lock the main loop.
        for (let hop = 0; hop <= this.blocks.length; hop++) {
            const block = this.blocks[blockIndex];
            if (!block) {
                return false;
            }

            const messageEntries = block.entries.filter(
                (entry) =>
                    entry.command === CON_COMMAND.NPC_SAY ||
                    entry.command === CON_COMMAND.NEXT_MESSAGE,
            );
            if (messageEntries.length > 0) {
                const entry = this.chooseMessageEntry(messageEntries);
                if (!entry) {
                    return false;
                }
                this.message = this.getString(entry.stringId);
                this.runReward(entry);
                blockIndex = entry.data;
                continue;
            }

            this.setOptions(block.entries);
            return Object.keys(this.options).length > 0;
        }

        throw new Error("CON dialog contains a message loop");
    }

    private chooseMessageEntry(entries: ConEntry[]): ConEntry | undefined {
        let hasKnownConditionalResult = false;
        for (const entry of entries) {
            if (!entry.conditionFunction) continue;
            const available = this.isAvailable(entry);
            if (available !== undefined) hasKnownConditionalResult = true;
            if (available === true) return entry;
        }

        const defaultEntry = entries.find((entry) => !entry.conditionFunction);
        if (defaultEntry) return defaultEntry;

        // An editor preview may deliberately omit the Lua evaluator. Keep the
        // base branch visible there without pretending conditional branches
        // passed. The game runtime always supplies the editable Lua source.
        if (!hasKnownConditionalResult) {
            return (
                entries.find((entry) => entry.conditionFunction !== "TA_hideMenu") ||
                entries[0]
            );
        }
        return undefined;
    }

    private setOptions(entries: ConEntry[]): void {
        this.options = {};
        this.optionEntries.clear();
        let optionId = 1;

        for (const entry of entries) {
            if (
                entry.command !== CON_COMMAND.CLOSE &&
                entry.command !== CON_COMMAND.PLAYER_SELECT &&
                entry.command !== CON_COMMAND.JUMP_SELECT
            ) {
                continue;
            }
            if (this.isAvailable(entry) !== true) continue;

            this.options[optionId] = this.getString(entry.stringId);
            this.optionEntries.set(optionId, entry);
            optionId++;
        }
    }

    private isAvailable(entry: ConEntry): boolean | undefined {
        if (!entry.conditionFunction) return true;
        return this.evaluateCondition(entry.conditionFunction);
    }

    private runReward(entry: ConEntry): any {
        if (entry.rewardFunction) {
            return this.runAction(entry.rewardFunction);
        }
        return undefined;
    }

    private finish(): void {
        const restartResult = this.initialEntry
            ? this.runReward(this.initialEntry)
            : undefined;
        if (Number(restartResult) >= 1) {
            if (this.openBlock(0)) return;
        }

        this.close();
    }

    private close(): void {
        this.closed = true;
        this.options = {};
        this.optionEntries.clear();
    }

    private getString(stringId: number): string {
        return this.strings[stringId] || `[String ID: ${stringId}]`;
    }
}
